/**
 * User roles list page – list municipality roles, filter archived.
 *
 * Phase 2.1–2.2 – USER_ROLES.md
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Empty, Table, Tag, Switch, Space, message, Modal } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMunicipalityRoles, useUpdateMunicipalityRole } from '../hooks/useMunicipalityRoles';
import type { MunicipalityRole } from '@municipality/types';

export const UserRoles: React.FC = () => {
  const { t } = useTranslation(['userRoles', 'common']);
  const navigate = useNavigate();
  const [includeArchived, setIncludeArchived] = useState(false);

  const { data: roles, isLoading } = useMunicipalityRoles({ includeArchived });
  const updateMutation = useUpdateMunicipalityRole();

  const handleEdit = useCallback(
    (role: MunicipalityRole) => {
      navigate(`/user-roles/edit/${role.id}`);
    },
    [navigate]
  );

  const handleArchiveToggle = useCallback(
    async (role: MunicipalityRole, archived: boolean) => {
      if (archived) {
        Modal.confirm({
          title: t('userRoles:archive.confirm'),
          content: t('userRoles:archive.confirmContent'),
          okText: t('common:buttons.save'),
          cancelText: t('common:buttons.cancel'),
          onOk: async () => {
            try {
              await updateMutation.mutateAsync({ roleId: role.id, input: { isArchived: true } });
              message.success(t('userRoles:archive.success'));
            } catch {
              message.error(t('userRoles:form.updateError'));
            }
          },
        });
        return;
      }
      try {
        await updateMutation.mutateAsync({ roleId: role.id, input: { isArchived: false } });
        message.success(t('userRoles:archive.unarchiveSuccess'));
      } catch {
        message.error(t('userRoles:form.updateError'));
      }
    },
    [updateMutation, t]
  );

  const columns = [
    {
      title: t('userRoles:list.name'),
      dataIndex: 'name',
      key: 'name',
      render: (_: unknown, record: MunicipalityRole) => (
        <Space>
          <span style={{ fontWeight: 500 }}>{record.name}</span>
          {record.isSystem && (
            <Tag color="blue">{t('userRoles:list.system')}</Tag>
          )}
          {record.isArchived && (
            <Tag color="default">{t('userRoles:list.archived')}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('userRoles:list.id'),
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => <code style={{ fontSize: 12 }}>{id}</code>,
    },
    {
      title: t('userRoles:list.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: t('userRoles:list.actions'),
      key: 'actions',
      width: 140,
      render: (_: unknown, record: MunicipalityRole) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('userRoles:list.edit')}
          </Button>
          {!record.isSystem && (
            <Switch
              size="small"
              checked={record.isArchived}
              onChange={(checked) => void handleArchiveToggle(record, checked)}
              checkedChildren={t('userRoles:list.archived')}
              unCheckedChildren={t('userRoles:list.active')}
            />
          )}
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Card loading />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0 }}>{t('userRoles:title')}</h2>
        <Space>
          <Switch
            checked={includeArchived}
            onChange={setIncludeArchived}
            checkedChildren={t('userRoles:list.filterArchived')}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/user-roles/create')}
          >
            {t('userRoles:list.createRole')}
          </Button>
        </Space>
      </div>

      <Card>
        {!roles?.length ? (
          <Empty
            description={
              <span>
                {t('userRoles:list.noRoles')}
                <br />
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                  {t('userRoles:list.noRolesDescription')}
                </span>
              </span>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/user-roles/create')}
            >
              {t('userRoles:list.createRole')}
            </Button>
          </Empty>
        ) : (
          <Table
            dataSource={roles}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 20, showSizeChanger: true }}
          />
        )}
      </Card>
    </div>
  );
};
